import { graphql, Link } from "gatsby"
import React, { useContext, useRef, useState } from "react"
import Col from "react-bootstrap/Col"
import Row from "react-bootstrap/Row"
import { useTranslation } from "react-i18next"

import ContactEmail from "@atoms/ContactEmail/ContactEmail"
import GoogleMapComponent from "@atoms/GoogleMapComponent/GoogleMapComponent"
import IconListItem from "@atoms/IconListItem/IconListItem"
import ModalWindow from "@atoms/ModalWindow/ModalWindow"
import PhoneNumber from "@atoms/PhoneNumber/PhoneNumber"
import Layout from "@components/layout.js"
import SEO from "@components/seo"
import { SiteContext } from "@context/Provider"
import "@lang/i18n"
import i18nWrapper from "@lang/i18nWrapper"
import Breadcrumb from "@molecules/Breadcrumb/Breadcrumb"
import Building from "@molecules/Building/Building"
import ContactCard from "@molecules/ContactCard/ContactCard"
import IconList from "@molecules/IconList/IconList"
import ImageZoomable from "@molecules/ImageZoomable/ImageZoomable"
import { useScrollPosition } from "@n8tb1t/use-scroll-position"
import ImageCarousel from "@organisms/ImageCarousel/ImageCarousel"

import keyToIcons from "./CampusDirections.json"
import styles from "./CampusDirections.module.scss"

const Campus = (props) => {
  const { t } = useTranslation()

  const markerImages = [
    "/media/markers/1.png",
    "/media/markers/2.png",
    "/media/markers/3.png",
    "/media/markers/4.png",
    "/media/markers/5.png",
    "/media/markers/6.png",
    "/media/markers/7.png",
    "/media/markers/8.png",
    "/media/markers/9.png",
  ]

  const mappedKeyToIcons = keyToIcons.map((group, i) => {
    let items = group.map((item, j) => {
      return (
        <IconListItem key={j} icon={item.icon}>
          {t(item.title)}
        </IconListItem>
      )
    })

    return (
      <Col key={i} xs="6" lg="3">
        <IconList>{items}</IconList>
      </Col>
    )
  })

  const [pinned, setPinned] = useState(true)
  const [keyToIconsModalOpen, setKeyToIconsModalOpen] = useState(false)

  const ref = useRef()

  useScrollPosition(
    ({ prevPos, currPos }) => {
      if (!ref.current) return

      let offsetTop = ref.current.getBoundingClientRect().top
      setPinned(offsetTop < 0)
    },
    [ref]
  )

  /* */

  const {
    data: { single: campus },
  } = props

  const {
    title,
    seoTitle,
    seoHeadJson,
    uri,
    campusDirectionsAbout,
    campusArrivalMap,
    campusArrivalMaps,
    campusArrivalAirportTextHTML,
    campusArrivalAirportLink,
    campusArrivalBusTextHTML,
    campusArrivalBusLink,
    campusArrivalCarTextHTML,
    campusArrivalCarLink,
    campusArrivalParkingTextHTML,
    campusArrivalParkingLink,
    campusArrivalTrainTextHTML,
    campusArrivalTrainLink,
    campusArrivalTramTextHTML,
    campusArrivalTramLink,
    campusArrivalMetroTextHTML,
    campusArrivalMetroLink,
    campusArrivalBikeTextHTML,
    campusArrivalBikeLink,
    campusArrivalAccessibilityTextHTML,
    campusArrivalAccessibilityLink,
    campusBuildings,
    campusEmail,
    campusTelephone,
    campusOpeningHours,
    wpParent,
    language,
    translations,
  } = campus

  const currentLanguage = language.slug

  // Context
  const context = useContext(SiteContext)
  context.currentLanguage = currentLanguage

  const markerData = (campusBuildings ? campusBuildings : [])
    .map((item) => {
      if (!item.location.lat) {
        return null
      }

      return {
        title: item.name.replace("Technopolis", "Technopolis <br />"),
        position: {
          lat: item.location.lat,
          lng: item.location.lng,
        },
        address: item.displayAddress,
        image: null,
        infoWindow: true,
      }
    })
    .filter((item) => {
      return item
    })

  const buildingCards = (campusBuildings ? campusBuildings : []).map(
    (item, i) => {
      return (
        <div key={i} className="base-margin-bottom-xs">
          <Building
            name={item.name}
            address={item.displayAddress}
            email={item.email}
            phone={item.telephone}
            openingHours={item.openingHours}
            directionsLink={item.directionsLink}
            location={item.location}
            marker={i + 1}
          />
        </div>
      )
    }
  )

  const about = (campusDirectionsAbout ? campusDirectionsAbout : "")
    .split("\n")
    .filter((item) => {
      return item.replace(/(\r\n|\n|\r)/gm, "")
    })
    .map((item, i) => {
      return item.trim() ? (
        <p key={i} className={i === 0 ? "type-h5" : "extra-line-height"}>
          {item}
        </p>
      ) : null
    })

  const languageRoot =
    currentLanguage === "en" ? "/" : "/" + currentLanguage + "/"

  return (
    <Layout
      translations={translations}
      currentLanguage={currentLanguage}
      customClass="has-google-map"
      lang={language.locale}
    >
      <SEO
        title={seoTitle}
        uri={uri}
        lang={language.locale}
        headJson={seoHeadJson}
        translations={translations}
      />

      <div
        className={`${styles.widgetContainer} make-full-width-on-mobile`}
        ref={ref}
      >
        <div className={styles.widget}>
          <div className="container">
            <div className={styles.content}>
              <Row>
                <div className="col-lg-8 col-xxl-7">
                  <div className={styles.breadcrumb}>
                    <Breadcrumb
                      ancestors={[
                        {
                          title: t("Home"),
                          url: languageRoot,
                        },
                        {
                          title: campus.areas
                            ? campus.areas.nodes[0]?.name
                            : null,
                          url: campus.areas ? campus.areas.nodes[0]?.uri : null,
                        },
                        {
                          title: wpParent?.node?.title,
                          url: wpParent?.node?.uri,
                        },
                      ]}
                      current={t("Directions")}
                    />
                  </div>

                  <div className="base-margin-bottom-small">
                    <h1 className={styles.widgetTitle}>{title}</h1>
                    {about}
                  </div>

                  {(campusArrivalMap || campusArrivalMaps.length > 0) && (
                    <div className={styles.block}>
                      <h2>{t("Arrival map")}</h2>

                      {campusArrivalMaps.length > 0 && (
                        <div className="base-margin-top base-margin-bottom">
                          <ImageCarousel
                            slides={campusArrivalMaps}
                            aspectRatio="7:5"
                            shadow={true}
                            zoomable={true}
                          />
                        </div>
                      )}

                      {campusArrivalMaps.length === 0 && (
                        <ImageZoomable
                          image={campusArrivalMap}
                          widths={[720, 960, 1440]}
                        />
                      )}

                      <div className="base-margin-bottom">
                        <button
                          className="no-button-styles arrow-link arrow-link-heavy black-link pointer-on-hover"
                          onClick={() => setKeyToIconsModalOpen(true)}
                        >
                          {t("View map symbols")}
                        </button>
                      </div>

                      <ModalWindow
                        open={keyToIconsModalOpen}
                        setOpen={setKeyToIconsModalOpen}
                        label={t("Map symbols explained")}
                        modalStyle={{ width: "120rem" }}
                      >
                        <h2 className="type-h4">
                          {t("Map symbols explained")}
                        </h2>
                        <Row className="base-margin-top-small">
                          {mappedKeyToIcons}
                        </Row>
                      </ModalWindow>
                    </div>
                  )}

                  <div className={styles.block}>
                    <h2 className="screen-reader-text">{t("Buildings")}</h2>
                    {buildingCards}
                  </div>

                  <div className={styles.block}>
                    <h2>{t("Contact us")}</h2>
                    <div style={{ maxWidth: "44rem" }}>
                      {campusOpeningHours && <p>{campusOpeningHours}</p>}
                      <ContactCard>
                        <ContactEmail>{campusEmail}</ContactEmail>
                        {campusTelephone && (
                          <PhoneNumber
                            number={campusTelephone}
                            person={campusEmail}
                          />
                        )}
                      </ContactCard>
                    </div>
                  </div>

                  <div className={styles.arrivals}>
                    {campusArrivalParkingTextHTML && (
                      <div className={styles.block}>
                        <img
                          className={styles.icon}
                          src="/media/svg/parking.svg"
                          alt=""
                          aria-hidden="true"
                        />
                        <h2>{t("Parking")}</h2>
                        <div
                          className="small-margins-for-paragraphs"
                          dangerouslySetInnerHTML={{
                            __html: campusArrivalParkingTextHTML,
                          }}
                        ></div>
                        {campusArrivalParkingLink?.uri &&
                          campusArrivalParkingLink.target !== "_blank" && (
                            <Link
                              to={campusArrivalParkingLink.uri}
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalParkingLink.title}
                            </Link>
                          )}
                        {campusArrivalParkingLink?.uri &&
                          campusArrivalParkingLink.target === "_blank" && (
                            <a
                              href={campusArrivalParkingLink.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalParkingLink.title}
                            </a>
                          )}
                      </div>
                    )}

                    {campusArrivalAirportTextHTML && (
                      <div className={styles.block}>
                        <img
                          className={styles.icon}
                          src="/media/svg/airport.svg"
                          alt=""
                          aria-hidden="true"
                        />
                        <h2>{t("From airport")}</h2>
                        <div
                          className="small-margins-for-paragraphs"
                          dangerouslySetInnerHTML={{
                            __html: campusArrivalAirportTextHTML,
                          }}
                        ></div>

                        {campusArrivalAirportLink?.uri &&
                          campusArrivalAirportLink.target !== "_blank" && (
                            <Link
                              to={campusArrivalAirportLink.uri}
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalAirportLink.title}
                            </Link>
                          )}
                        {campusArrivalAirportLink?.uri &&
                          campusArrivalAirportLink.target === "_blank" && (
                            <a
                              href={campusArrivalAirportLink.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalAirportLink.title}
                            </a>
                          )}
                      </div>
                    )}

                    {campusArrivalCarTextHTML && (
                      <div className={styles.block}>
                        <img
                          className={styles.icon}
                          src="/media/svg/car.svg"
                          alt=""
                          aria-hidden="true"
                        />
                        <h2>{t("By car")}</h2>
                        <div
                          className="small-margins-for-paragraphs"
                          dangerouslySetInnerHTML={{
                            __html: campusArrivalCarTextHTML,
                          }}
                        ></div>

                        {campusArrivalCarLink?.uri &&
                          campusArrivalCarLink.target !== "_blank" && (
                            <Link
                              to={campusArrivalCarLink.uri}
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalCarLink.title}
                            </Link>
                          )}
                        {campusArrivalCarLink?.uri &&
                          campusArrivalCarLink.target === "_blank" && (
                            <a
                              href={campusArrivalCarLink.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalCarLink.title}
                            </a>
                          )}
                      </div>
                    )}

                    {campusArrivalBusTextHTML && (
                      <div className={styles.block}>
                        <img
                          className={styles.icon}
                          src="/media/svg/bus.svg"
                          alt=""
                          aria-hidden="true"
                        />
                        <h2>{t("By bus")}</h2>
                        <div
                          className="small-margins-for-paragraphs"
                          dangerouslySetInnerHTML={{
                            __html: campusArrivalBusTextHTML,
                          }}
                        ></div>

                        {campusArrivalBusLink?.uri &&
                          campusArrivalBusLink.target !== "_blank" && (
                            <Link
                              to={campusArrivalBusLink.uri}
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalBusLink.title}
                            </Link>
                          )}
                        {campusArrivalBusLink?.uri &&
                          campusArrivalBusLink.target === "_blank" && (
                            <a
                              href={campusArrivalBusLink.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalBusLink.title}
                            </a>
                          )}
                      </div>
                    )}

                    {campusArrivalTrainTextHTML && (
                      <div className={styles.block}>
                        <img
                          className={styles.icon}
                          src="/media/svg/train.svg"
                          alt=""
                          aria-hidden="true"
                        />
                        <h2>{t("By train")}</h2>
                        <div
                          className="small-margins-for-paragraphs"
                          dangerouslySetInnerHTML={{
                            __html: campusArrivalTrainTextHTML,
                          }}
                        ></div>

                        {campusArrivalTrainLink?.uri &&
                          campusArrivalTrainLink.target !== "_blank" && (
                            <Link
                              to={campusArrivalTrainLink.uri}
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalTrainLink.title}
                            </Link>
                          )}
                        {campusArrivalTrainLink?.uri &&
                          campusArrivalTrainLink.target === "_blank" && (
                            <a
                              href={campusArrivalTrainLink.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalTrainLink.title}
                            </a>
                          )}
                      </div>
                    )}

                    {campusArrivalTramTextHTML && (
                      <div className={styles.block}>
                        <img
                          className={styles.icon}
                          src="/media/svg/tram.svg"
                          alt=""
                          aria-hidden="true"
                        />
                        <h2>{t("By tram")}</h2>
                        <div
                          className="small-margins-for-paragraphs"
                          dangerouslySetInnerHTML={{
                            __html: campusArrivalTramTextHTML,
                          }}
                        ></div>

                        {campusArrivalTramLink?.uri &&
                          campusArrivalTramLink.target !== "_blank" && (
                            <Link
                              to={campusArrivalTramLink.uri}
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalTramLink.title}
                            </Link>
                          )}
                        {campusArrivalTramLink?.uri &&
                          campusArrivalTramLink.target === "_blank" && (
                            <a
                              href={campusArrivalTramLink.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalTramLink.title}
                            </a>
                          )}
                      </div>
                    )}

                    {campusArrivalMetroTextHTML && (
                      <div className={styles.block}>
                        <img
                          className={styles.icon}
                          src="/media/svg/metro.svg"
                          alt=""
                          aria-hidden="true"
                        />
                        <h2>{t("By metro")}</h2>
                        <div
                          className="small-margins-for-paragraphs"
                          dangerouslySetInnerHTML={{
                            __html: campusArrivalMetroTextHTML,
                          }}
                        ></div>
                        {campusArrivalMetroLink?.uri &&
                          campusArrivalMetroLink.target !== "_blank" && (
                            <Link
                              to={campusArrivalMetroLink.uri}
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalMetroLink.title}
                            </Link>
                          )}
                        {campusArrivalMetroLink?.uri &&
                          campusArrivalMetroLink.target === "_blank" && (
                            <a
                              href={campusArrivalMetroLink.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalMetroLink.title}
                            </a>
                          )}
                      </div>
                    )}

                    {campusArrivalBikeTextHTML && (
                      <div className={styles.block}>
                        <img
                          className={styles.icon}
                          src="/media/svg/bike.svg"
                          alt=""
                          aria-hidden="true"
                        />
                        <h2>{t("By bike")}</h2>
                        <div
                          className="small-margins-for-paragraphs"
                          dangerouslySetInnerHTML={{
                            __html: campusArrivalBikeTextHTML,
                          }}
                        ></div>
                        {campusArrivalBikeLink?.uri &&
                          campusArrivalBikeLink.target !== "_blank" && (
                            <Link
                              to={campusArrivalBikeLink.uri}
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalBikeLink.title}
                            </Link>
                          )}
                        {campusArrivalBikeLink?.uri &&
                          campusArrivalBikeLink.target === "_blank" && (
                            <a
                              href={campusArrivalBikeLink.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalBikeLink.title}
                            </a>
                          )}
                      </div>
                    )}

                    {campusArrivalAccessibilityTextHTML && (
                      <div className={styles.block}>
                        <img
                          className={styles.icon}
                          src="/media/svg/accessibility.svg"
                          alt=""
                          aria-hidden="true"
                        />
                        <h2>{t("Accessibility")}</h2>
                        <div
                          className="small-margins-for-paragraphs"
                          dangerouslySetInnerHTML={{
                            __html: campusArrivalAccessibilityTextHTML,
                          }}
                        ></div>
                        {campusArrivalAccessibilityLink?.uri &&
                          campusArrivalAccessibilityLink.target !==
                            "_blank" && (
                            <Link
                              to={campusArrivalMetroLink.uri}
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalAccessibilityLink.title}
                            </Link>
                          )}
                        {campusArrivalAccessibilityLink?.uri &&
                          campusArrivalAccessibilityLink.target ===
                            "_blank" && (
                            <a
                              href={campusArrivalAccessibilityLink.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="arrow-link arrow-link-heavy black-link"
                            >
                              {campusArrivalAccessibilityLink.title}
                            </a>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </Row>
            </div>
          </div>

          <div
            className={`${styles.map} ${
              pinned ? styles.pinned : styles.unpinned
            }`}
          >
            <div>
              {markerData.length > 0 && (
                <GoogleMapComponent
                  markers={markerData}
                  numbers={markerImages}
                  height="100%"
                  markerSize={56}
                  defaultZoom={16}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default i18nWrapper("Campus")(Campus)

export const query = graphql`
  query SINGLE_CAMPUS_DIRECTIONS($id: String!) {
    single: wpCampus(id: { eq: $id }) {
      title
      seoTitle
      seoHeadJson
      uri
      language {
        slug
        locale
      }
      translations {
        title
        uri
        language {
          slug
          locale
        }
      }
      areas {
        nodes {
          slug
          name
          uri
        }
      }
      #############
      campusDirectionsAbout
      campusDirectionsExcerpt

      campusArrivalMaps {
        dominantColor
        sourceUrl: sourceUrlLarge
        mediaDetails: mediaDetailsLarge {
          width
          height
        }
      }

      campusArrivalMap {
        dominantColor
        sourceUrl: sourceUrlLarge
        mediaDetails: mediaDetailsLarge {
          width
          height
        }
      }

      campusArrivalAirportTextHTML
      campusArrivalAirportLink {
        target
        title
        uri
      }
      campusArrivalBusTextHTML
      campusArrivalBusLink {
        target
        title
        uri
      }
      campusArrivalCarTextHTML
      campusArrivalCarLink {
        target
        title
        uri
      }
      campusArrivalParkingTextHTML
      campusArrivalParkingLink {
        target
        title
        uri
      }
      campusArrivalTrainTextHTML
      campusArrivalTrainLink {
        target
        title
        uri
      }
      campusArrivalTramTextHTML
      campusArrivalTramLink {
        target
        title
        uri
      }
      campusArrivalMetroTextHTML
      campusArrivalMetroLink {
        target
        title
        uri
      }
      campusArrivalBikeTextHTML
      campusArrivalBikeLink {
        target
        title
        uri
      }
      campusArrivalAccessibilityTextHTML
      campusArrivalAccessibilityLink {
        target
        title
        uri
      }
      campusBuildings {
        email
        location {
          lat
          lng
        }
        name
        directionsLink
        openingHours
        telephone
        displayAddress
      }
      campusEmail
      campusTelephone
      campusOpeningHours
      wpParent {
        node {
          id
          uri
          ... on WpCampus {
            title
          }
        }
      }
    }
  }
`
